import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent,
  Box, Typography, Button, IconButton, CircularProgress, Tooltip,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon         from '@mui/icons-material/Close';
import LocalText from '../common/LocalText';
import ConfirmModal from '../form-components/ConfirmModal';
import { toLocalDateStr, todayEastern } from '../../utils/dateUtils';

// ── Constants ─────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ── Calendar grid builder ─────────────────────────────────────────────────────
// Padding cells outside the campaign range stay in `cells` (inCampaign=false) so the week grid aligns.

const buildMonthGrid = (startStr, endStr) => {
  if (!startStr || !endStr) return [];

  const campaignStart = new Date(startStr + 'T00:00:00');
  const campaignEnd   = new Date(endStr   + 'T00:00:00');
  const months        = [];

  // Walk month by month from the campaign start month
  let cursor = new Date(campaignStart.getFullYear(), campaignStart.getMonth(), 1);

  while (cursor <= campaignEnd) {
    const year  = cursor.getFullYear();
    const month = cursor.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth  = new Date(year, month + 1, 0);

    // Pad the grid backwards to the Sunday that contains the 1st
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - gridStart.getDay());

    // Pad the grid forward to the Saturday that contains the last day
    const gridEnd = new Date(lastOfMonth);
    gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));

    const cells = [];
    const d = new Date(gridStart);
    while (d <= gridEnd) {
      cells.push({
        date:       toLocalDateStr(d),
        // Leading/trailing padding days that fall in the previous/next month are
        // rendered blank here — they already appear as real cells in that month's
        // own section, so keeping them "inCampaign" here would show them twice.
        inMonth:    d.getMonth() === month,
        inCampaign: d.getMonth() === month && d >= campaignStart && d <= campaignEnd,
      });
      d.setDate(d.getDate() + 1);
    }

    months.push({
      key:   `${year}-${String(month + 1).padStart(2, '0')}`,
      label: `${MONTH_NAMES[month]} ${year}`,
      cells,
    });

    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
};

// ── Day Cell ──────────────────────────────────────────────────────────────────

const DayCell = ({ date, avail, adDurationSeconds, isSelected, onToggle, disabled, isPast = false }) => {
  const dur       = Number(adDurationSeconds) || 0;
  const used      = avail?.usedSeconds      ?? 0;
  const available = avail?.availableSeconds ?? 86400;
  // A past day is history: it can't be added or removed, and its capacity no longer matters.
  const isFull    = !isPast && dur > 0 && available < dur;
  const usedPct   = Math.min(100, (used / 86400) * 100);
  const dayNum    = new Date(date + 'T00:00:00').getDate();
  const canClick  = !isFull && !disabled && !isPast;

  const cell = (
    <Box
      onClick={canClick ? onToggle : undefined}
      sx={{
        position:  'relative',
        height:    68,
        borderRadius: '8px',
        overflow:  'hidden',
        border:    isSelected && !isFull
          ? '2px solid #0a326d'
          : isFull
            ? '1px solid #efb4a4'
            : '1px solid #dde6f0',
        bgcolor:   isSelected && !isFull ? '#dbeafe' : '#fff',
        cursor:    canClick ? 'pointer' : isPast ? 'default' : 'not-allowed',
        opacity:   isFull ? 0.52 : isPast ? 0.6 : 1,
        userSelect: 'none',
        transition: 'border-color 0.1s, background-color 0.1s',
        '&:hover': canClick
          ? { borderColor: '#5383b3', bgcolor: isSelected ? '#dbeafe' : '#eef3fd' }
          : {},
      }}
    >
      {/* Liquid fill — rises from the bottom proportional to used % of 86 400 s */}
      {usedPct > 0 && (
        <Box
          sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: `${usedPct}%`,
            bgcolor: isFull ? 'rgba(239,154,154,0.30)' : 'rgba(83,131,179,0.15)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Cell content */}
      <Box
        sx={{
          position: 'relative', height: '100%',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', px: 0.3,
        }}
      >
        {/* Day number — centered in remaining space */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            sx={{
              fontWeight: 700, fontSize: 16, lineHeight: 1,
              color: isSelected && !isFull ? '#0a326d' : '#1e3a5f',
            }}
          >
            {dayNum}
          </Typography>
        </Box>

        {/* % / Free / Full — pinned to bottom */}
        <Typography
          sx={{
            fontSize: 9, fontWeight: 700, lineHeight: 1, pb: 0.8,
            color: isFull ? '#e65100' : isSelected ? '#0a326d' : '#5383b3',
          }}
        >
          {isPast ? 'Past' : isFull ? 'Full' : used === 0 ? 'Free' : `${usedPct.toFixed(2)}%`}
        </Typography>
      </Box>
    </Box>
  );

  return isPast
    ? <Tooltip title="Past dates can't be changed" placement="top" arrow>{cell}</Tooltip>
    : cell;
};

// ── Main Component ────────────────────────────────────────────────────────────

const AdScheduleCalendarPicker = ({
  campaignStartDate,
  campaignEndDate,
  adDurationSeconds,
  availabilityData,
  selectedDates,
  onChange,
  disabled       = false,
  loading        = false,
  placementLabel = '',
  required       = false,
  today: todayProp,
}) => {
  const [calOpen, setCalOpen] = useState(false);
  // Days before today (US Eastern) are history: shown, never editable.
  const today = todayProp ?? todayEastern();
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const months = useMemo(
    () => buildMonthGrid(campaignStartDate, campaignEndDate),
    [campaignStartDate, campaignEndDate],
  );

  const availMap = useMemo(() => {
    const m = {};
    availabilityData.forEach((d) => { m[d.runDate] = d; });
    return m;
  }, [availabilityData]);

  const selectedSet = useMemo(() => new Set(selectedDates), [selectedDates]);

  // Selected days that are already past stay selected through Select all / Clear.
  const lockedPast     = useMemo(() => selectedDates.filter((d) => d < today), [selectedDates, today]);
  const removableCount = selectedDates.length - lockedPast.length;

  // Flat list of all campaign dates (used by Select All)
  const allCampaignDays = useMemo(
    () => months.flatMap((m) => m.cells).filter((c) => c.inCampaign).map((c) => c.date),
    [months],
  );

  const toggle = (date) => {
    if (disabled || date < today) return;
    const avail = availMap[date];
    const dur   = Number(adDurationSeconds) || 0;
    if (avail && avail.availableSeconds < dur) return;
    const next = new Set(selectedSet);
    if (next.has(date)) next.delete(date); else next.add(date);
    onChange([...next].sort());
  };

  const handleSelectAll = () => {
    const dur   = Number(adDurationSeconds) || 0;
    const picks = allCampaignDays.filter((date) => {
      if (date < today) return false;
      const a = availMap[date];
      return !(dur > 0 && (a?.availableSeconds ?? 86400) < dur);
    });
    onChange([...new Set([...lockedPast, ...picks])].sort());
  };

  const handleClearAll = () => {
    if (removableCount === 0) return;
    setClearConfirmOpen(true);
  };

  const confirmClearAll = () => {
    onChange(lockedPast);
    setClearConfirmOpen(false);
  };

  const count        = selectedDates.length;
  const hasSelection = count > 0;

  return (
    <>
      {/* ── Trigger ── */}
      <Box
        onClick={() => { if (!loading) setCalOpen(true); }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.2,
          border: `1.5px dashed ${hasSelection ? '#0a326d' : '#c8d8ea'}`,
          borderRadius: '10px', px: 2, py: 1.2,
          cursor: loading ? 'default' : 'pointer',
          bgcolor: hasSelection ? '#edf2fb' : '#f7faff',
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover': !loading
            ? { borderColor: '#5383b3', bgcolor: hasSelection ? '#e8f0fd' : '#f0f5ff' }
            : {},
        }}
      >
        {loading
          ? <CircularProgress size={15} sx={{ color: '#5383b3', flexShrink: 0 }} />
          : <CalendarMonthIcon sx={{ fontSize: 17, color: '#5383b3', flexShrink: 0 }} />}

        <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: loading ? '#85a5c6' : hasSelection ? '#0a326d' : '#85a5c6' }}>
          {loading
            ? <LocalText>Loading availability…</LocalText>
            : hasSelection
              ? <LocalText>{`${count} day${count !== 1 ? 's' : ''} selected — click to edit`}</LocalText>
              : <LocalText>Click to select run dates</LocalText>}
        </Typography>
      </Box>
      {required && !loading && !hasSelection && (
        <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.5, px: 0.25 }}>
          <LocalText>Required</LocalText>
        </Typography>
      )}

      {/* ── Calendar Dialog ── */}
      <Dialog
        open={calOpen}
        onClose={() => setCalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: 1.5, overflow: 'hidden' } }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: '#0a326d', color: 'white',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            py: 1.8, px: 2.5,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
              <LocalText>Select Run Dates</LocalText>
            </Typography>
            {placementLabel && (
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, mt: 0.3 }}>
                {placementLabel}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setCalOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.7)', mt: -0.3, '&:hover': { color: 'white' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Weekday column headers */}
        <Box
          sx={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5,
            px: 2, pt: 1.5, pb: 0.8,
            bgcolor: 'white', borderBottom: '1px solid #eaf0f6',
          }}
        >
          {WEEKDAYS.map((d) => (
            <Typography
              key={d}
              sx={{
                textAlign: 'center', color: '#85a5c6',
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
              }}
            >
              {d}
            </Typography>
          ))}
        </Box>

        {/* Month grids */}
        <DialogContent sx={{ px: 2, pt: 1.5, pb: 1, maxHeight: 420, overflowY: 'auto' }}>
          {months.length === 0 && (
            <Typography sx={{ color: '#85a5c6', fontSize: 12, textAlign: 'center', py: 4 }}>
              <LocalText>No dates in campaign range</LocalText>
            </Typography>
          )}

          {months.map((month) => (
            <Box key={month.key} sx={{ mb: 2 }}>
              {/* Month label */}
              <Typography sx={{
                display: 'block', fontWeight: 700, color: '#5383b3',
                fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.8,
              }}>
                {month.label}
              </Typography>

              {/* 7-column day grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
                {month.cells.map(({ date, inMonth, inCampaign }) =>
                  !inMonth ? (
                    <Box key={date} sx={{ height: 68 }} />
                  ) : inCampaign ? (
                    <DayCell
                      key={date}
                      date={date}
                      avail={availMap[date]}
                      adDurationSeconds={adDurationSeconds}
                      isSelected={selectedSet.has(date)}
                      onToggle={() => toggle(date)}
                      disabled={disabled}
                      isPast={date < today}
                    />
                  ) : (
                    <Tooltip key={date} title="Outside campaign dates" placement="top" arrow>
                      <Box
                        sx={{
                          height: 68, borderRadius: '8px',
                          border: '1px dashed #dde6f0',
                          bgcolor: '#f5f7fa',
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'space-between',
                          py: 0.7, cursor: 'default', opacity: 0.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#b0bec5' }}>
                          {new Date(date + 'T00:00:00').getDate()}
                        </Typography>
                        <Typography sx={{
                          fontSize: 7.5, fontWeight: 700, color: '#b0bec5',
                          textTransform: 'uppercase', letterSpacing: 0.3, textAlign: 'center', px: 0.3,
                        }}>
                          Not in campaign
                        </Typography>
                      </Box>
                    </Tooltip>
                  ),
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2.5, py: 1.5, borderTop: '1px solid #eaf0f6', bgcolor: 'grey.50',
          }}
        >
          {/* Select all / Clear — hidden in read-only mode */}
          {!disabled ? (
            <Box display="flex" gap={0.5}>
              <Button
                size="small"
                onClick={handleSelectAll}
                sx={{ fontSize: 11, textTransform: 'none', color: '#5383b3', fontWeight: 600, minWidth: 0 }}
              >
                <LocalText>Select all</LocalText>
              </Button>
              <Button
                size="small"
                onClick={handleClearAll}
                sx={{ fontSize: 11, textTransform: 'none', color: '#85a5c6', fontWeight: 600, minWidth: 0 }}
              >
                <LocalText>Clear</LocalText>
              </Button>
            </Box>
          ) : <Box />}

          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: count === 0 ? '#d97706' : '#5383b3' }}>
              {count} day{count !== 1 ? 's' : ''} selected
            </Typography>
            {lockedPast.length > 0 && (
              <Typography sx={{ fontSize: 10, color: '#85a5c6' }}>
                {lockedPast.length} already past, can't be changed
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            onClick={() => setCalOpen(false)}
            sx={{
              bgcolor: '#0a326d', color: 'white', fontWeight: 700,
              textTransform: 'none', borderRadius: '8px', px: 2.5, fontSize: 12,
              boxShadow: 'none', '&:hover': { bgcolor: '#0d3d85' },
            }}
          >
            <LocalText>Done</LocalText>
          </Button>
        </Box>
      </Dialog>

      <ConfirmModal
        open={clearConfirmOpen}
        onCancel={() => setClearConfirmOpen(false)}
        onConfirm={confirmClearAll}
        title="Clear run dates?"
        message={`This will remove ${removableCount} selected day${removableCount !== 1 ? 's' : ''}${lockedPast.length > 0 ? ` (${lockedPast.length} past day${lockedPast.length !== 1 ? 's are' : ' is'} kept)` : ''}.`}
        subtitle="You'll need to reselect dates before this ad can be saved."
        confirmLabel="Clear"
        severity="danger"
      />
    </>
  );
};

export default AdScheduleCalendarPicker;
